import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useT } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayoutOnboarding() {
  const { token } = useParams();
  const t = useT();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/payout-onboarding/${token}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setLoadError(e?.response?.data?.detail || e.message); });
    return () => { alive = false; };
  }, [token]);

  const setField = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const validate = () => {
    if (!data?.schema) return t("onboarding.validate.no_schema");
    for (const f of data.schema) {
      const v = (values[f.name] || "").trim();
      if (f.required && !v) return t("onboarding.validate.required", { field: f.label });
      if (f.pattern && v && !new RegExp(f.pattern).test(v)) {
        return t("onboarding.validate.format", { field: f.label, pattern: f.pattern });
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const err = validate();
    if (err) { setSubmitError(err); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/public/payout-onboarding/${token}/submit`,
        { bank_account: values },
      );
      setResult(r.data);
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || t("onboarding.submit_error_default"));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="max-w-xl mx-auto px-6 py-16">
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-6" data-testid="onboarding-error">
            <h1 className="text-2xl font-serif text-rose-900">{t("onboarding.invalid.title")}</h1>
            <p className="mt-3 text-sm text-rose-800">{loadError}</p>
            <p className="mt-3 text-xs text-rose-700">{t("onboarding.invalid.hint")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="max-w-xl mx-auto px-6 py-16 text-stone-500 text-sm">{t("onboarding.loading")}</div>
      </div>
    );
  }

  if (data.recipient.already_completed || result?.already_completed) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="max-w-xl mx-auto px-6 py-16">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="onboarding-complete">
            <h1 className="text-2xl font-serif text-emerald-900">{t("onboarding.complete.title")}</h1>
            <p className="mt-3 text-sm text-emerald-800">{t("onboarding.complete.desc")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (result?.ok && result?.ready_to_pay) {
    const auto = result?.auto_payout;
    const autoFired = auto?.attempted && auto?.ok;
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="max-w-xl mx-auto px-6 py-16">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="onboarding-success">
            <h1 className="text-2xl font-serif text-emerald-900">{t("onboarding.success.title")}</h1>
            <p className="mt-3 text-sm text-emerald-800">
              {autoFired ? t("onboarding.success.auto_paid") : t("onboarding.success.pending")}
            </p>
            {autoFired && auto.stripe_payout_id && (
              <p className="mt-3 text-[11px] text-emerald-700 font-mono" data-testid="onboarding-stripe-payout-id">
                {t("onboarding.success.ref", { id: auto.stripe_payout_id })}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const dep = data.deposit;
  const amountStr = `${dep.currency} ${Number(dep.amount).toFixed(2)}`;
  const recipName = data.recipient.name || t("onboarding.fallback_name");

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <div className="max-w-xl mx-auto px-6 py-12" data-testid="payout-onboarding-page">
        <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500">{t("onboarding.eyebrow")}</p>
        <h1 className="text-3xl font-serif mt-2 text-stone-900" data-testid="onboarding-title">
          {t("onboarding.title", { name: recipName })}
        </h1>
        <p className="mt-3 text-sm text-stone-700 leading-relaxed">
          <b>{dep.sender_name}</b> {t("onboarding.intro_via")} <b>{amountStr}</b>
          {dep.reference_number ? <> {t("onboarding.intro_ref_open")} <span className="font-mono">{dep.reference_number}</span>{t("onboarding.intro_ref_close")}</> : null}.
        </p>
        <p className="mt-2 text-sm text-stone-700 leading-relaxed">
          {t("onboarding.country_intro", { country: data.recipient.country })}
        </p>

        {!data.feature_active && (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900" data-testid="onboarding-dormant-warning">
            <b>{t("onboarding.beta.title")}</b> {t("onboarding.beta.body")}
          </div>
        )}

        <div className="mt-6 space-y-4">
          {data.schema.map((f) => (
            <div key={f.name} data-testid={`onboarding-field-${f.name}`}>
              <Label htmlFor={`f-${f.name}`} className="text-[12px] tracking-wide uppercase text-stone-600">
                {f.label}{f.required && <span className="text-rose-500"> *</span>}
              </Label>
              {f.type === "select" ? (
                <Select value={values[f.name] || ""} onValueChange={(v) => setField(f.name, v)}>
                  <SelectTrigger id={`f-${f.name}`} className="mt-1.5" data-testid={`onboarding-select-${f.name}`}>
                    <SelectValue placeholder={t("onboarding.select_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(f.options || []).map((o) => (
                      <SelectItem key={o} value={o} data-testid={`onboarding-option-${f.name}-${o}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`f-${f.name}`}
                  className="mt-1.5"
                  value={values[f.name] || ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  data-testid={`onboarding-input-${f.name}`}
                  autoComplete="off"
                />
              )}
              {f.help && <p className="mt-1 text-[11px] text-stone-500">{f.help}</p>}
            </div>
          ))}
        </div>

        {submitError && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-900" data-testid="onboarding-submit-error">
            {submitError}
          </div>
        )}

        <Button
          className="mt-6 w-full"
          onClick={handleSubmit}
          disabled={busy}
          data-testid="onboarding-submit-btn"
        >
          {busy ? t("onboarding.submit_busy") : t("onboarding.submit")}
        </Button>

        <p className="mt-4 text-[11px] text-stone-500 text-center">
          {t("onboarding.secure_note")}
        </p>
      </div>
    </div>
  );
}
